import Button from "@atlas/uicorn/lib/Control/Button";
import React, { useEffect, useMemo, useRef, useState } from "react";
import Modal from "@atlas/uicorn/lib/Display/Modal";
import Form from "@atlas/uicorn/lib/Control/Form";
import FormItem, {
  type TFormItemRule,
} from "@atlas/uicorn/lib/Control/FormItem";
import type { TRichInputValue } from "@atlas/uicorn/lib/Input/RichInput";
import serialize from "@atlas/tool/box/slate/serialize";
import Text from "@atlas/uicorn/lib/Display/Text";
import {
  CustomerPortalFormFieldCategory,
  TSupportPortalForm,
  TSupportPortalFormField,
} from "../../lib/load-company-settings";
import loadCustomFields, { CustomField } from "../../lib/load-custom-fields";
import TicketFormField from "./ticket-form-field";
import useConversationsListContext from "./customer-portal-context";
import { AppSettings } from "../../lib/get-app-settings";
import Composer, { TNewComposerRef } from "./conversation/composer/composer";
import styles from "../../styles/create-ticket-modal.module.css";
import cn from "classnames";
import InfoIcon from "@atlas/uicorn/lib/Icon/InfoIcon";
import useRefCallback from "use-ref-callback";
import evaluate from "@atlas/tool/box/conditional-fields/evaluate";
import getDependents from "@atlas/tool/box/conditional-fields/dependents";
import determineFieldDisabledStatus from "@atlas/tool/box/conditional-fields/field";
import notify from "@atlas/tool/box/ui/notify";
import t from "../../texts/t";
import ComposerHeaderField from "./conversation/composer/header-field";
import CcField from "./conversation/composer/cc-field";
import { DeliveryType } from "../../models/conversation";
import IconButton from "@atlas/uicorn/lib/Control/IconButton";

const CreateTicketModal = function CreateTicketModal({
  appSettings,
  formConfig,
  onClose,
}: TCreateTicketModalProps) {
  const [form] = Form.useForm();
  const values = Form.useWatch([], form);
  const [value, setValue] = useState<TRichInputValue>();
  const [ccEmails, setCcEmails] = useState<string[]>([]);

  const [customFields, setCustomFields] = useState<CustomField[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const composerRef = useRef<TNewComposerRef>(null);

  const { createConversation } = useConversationsListContext();

  useEffect(() => {
    loadCustomFields(appSettings.appId).then(setCustomFields);
  }, [appSettings.appId]);

  const handleSubmit = (data: Record<string, any>) => {
    const { message, ...restFields } = data;
    const messageText = descendantsToString(message);
    const attachments = composerRef.current?.getAttachments() || [];
    setSubmitting(true);

    return createConversation({
      formId: formConfig.id,
      key: formConfig.key,
      attachments,
      copiedCustomers: ccEmails,
      fields: {
        message: messageText,
        ...restFields,
      },
    })
      .then(onClose)
      .catch(() => {
        onClose();
        notify.error({
          message: t(
            "Sorry, an error occurred while creating the ticket",
            appSettings.language
          ),
        });
      })
      .finally(() => setSubmitting(false));
  };

  const evaluateDependents = useRefCallback(
    (formField: TSupportPortalFormField, customField?: CustomField) => {
      if (!customField) return formField;

      const hasDependents =
        customField.dependsOn?.length && customField.dependsOn.length > 0;
      if (!hasDependents) return formField;

      const formValues: Record<string, unknown> = {};

      Object.keys(values).forEach((key) => {
        const cf = customFields.find((f) => f.key === key);
        if (!cf) {
          formValues[key] = values[key];
        } else {
          const newKey = `customFields.${key}`;
          formValues[newKey] = values[key];
        }
      });

      const result = evaluate({ customField, ticket: formValues });

      if (!Array.isArray(result.value)) return formField;

      return {
        ...formField,
        options: result.value as string[],
      };
    }
  );

  const customFieldsValues = useMemo(
    () =>
      Object.keys(values || {}).map((key) => {
        const name = formConfig.fields.find((f) => f.key === key)?.label || key;
        return { key, name, value: values[key] };
      }),
    [values, formConfig.fields]
  );

  const showHeader = formConfig.channel === DeliveryType.EMAIL;

  return (
    <Modal
      header={formConfig.title}
      onClose={onClose}
      footer={
        <Button
          type="primary"
          loading={submitting && "prefix"}
          onClick={() => form.submit()}
        >
          {t("Create ticket", appSettings.language)}
        </Button>
      }
    >
      <Form
        form={form}
        onFinish={handleSubmit}
        layout="vertical"
        focusFirstInput
      >
        {formConfig.description && (
          <Text p color="grey8">
            {formConfig.description}
          </Text>
        )}

        {formConfig.fields.map((field) => {
          const validationRules: TFormItemRule[] = [
            {
              required: mandatoryFields.includes(field.key) || field.required,
              message: t("{0} is required", appSettings.language, [
                field.label,
              ]),
            },
            ...(field.key === "email"
              ? [
                  {
                    type: "email",
                    message: t("Invalid email", appSettings.language),
                  } as TFormItemRule,
                ]
              : []),
          ];

          const customField = customFields.find((cf) => cf.key === field.key);

          const { hint, disabled } = determineFieldDisabledStatus({
            customField,
            customFieldsValues,
          });

          const fieldsToReset =
            field.type === "custom"
              ? getDependents(
                  customFields.map((cf) => ({
                    ...cf,
                    key: `${cf.category}.customFields.${cf.key}`,
                  })),
                  `${categoryToEntity[field.category]}.customFields.${
                    field.key
                  }`
                ).reduce((fields, fieldKey) => {
                  const [_, __, key] = fieldKey.split(".");
                  fields[key] = undefined;
                  return fields;
                }, {} as Record<string, undefined>)
              : [];

          const depsValues = (customField?.dependsOn || []).map((dependent) => {
            const [_, __, dependentKey] = dependent.split(".");
            return values && values[dependentKey];
          });

          return (
            <FormItem
              key={`${field.key}--${depsValues.join(",")}`}
              label={
                <div
                  className={cn(styles.formItemLabel, {
                    [styles.disabled]: disabled,
                  })}
                >
                  {field.label}{" "}
                  {disabled && (
                    <IconButton icon={<InfoIcon />} tooltip={hint} />
                  )}
                </div>
              }
              name={field.key}
              rules={validationRules}
            >
              {field.key === "message" ? (
                <Composer
                  ref={composerRef}
                  className={styles.composer}
                  placeholder={t("Add your message...", appSettings.language)}
                  defaultValue={value}
                  onChange={setValue}
                  supportAttachments
                  header={
                    showHeader && (
                      <ComposerHeaderField label="CC">
                        <CcField
                          emails={ccEmails}
                          onChange={(emails) =>
                            Promise.resolve(setCcEmails(emails))
                          }
                        />
                      </ComposerHeaderField>
                    )
                  }
                />
              ) : (
                <TicketFormField
                  field={evaluateDependents(field, customField)}
                  customFields={customFields}
                  disabled={disabled}
                  onChange={(value) => {
                    field.type === "custom" &&
                      form.setFieldsValue({
                        ...values,
                        ...fieldsToReset,
                        [field.key]: value,
                      });
                  }}
                />
              )}
            </FormItem>
          );
        })}
      </Form>
    </Modal>
  );
};

export default CreateTicketModal;

const mandatoryFields = ["subject", "message"];

type TCreateTicketModalProps = {
  appSettings: AppSettings;
  onClose: () => void;
  formConfig: TSupportPortalForm;
};

export const descendantsToString = (value: TRichInputValue) =>
  value.reduce((t, node) => t + serialize(node, { helpCenterUrl: "" }), "");

const categoryToEntity = {
  [CustomerPortalFormFieldCategory.CONVERSATION]: "ticket",
  [CustomerPortalFormFieldCategory.CUSTOMER]: "customer",
};
